import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import {
  Upload, Database, LayoutDashboard, MessageSquare, BarChart3,
  ArrowRight, ChevronRight, Check, Shield, Zap, Lock, KeyRound, FileCheck2, Activity,
  Users, DollarSign, Search, Sparkles, FileSpreadsheet, LineChart, Brain,
  TrendingUp, Loader2, Cloud, Network, GitBranch, Eye, ScrollText, ServerCog,
  Clock, Quote, Plug, Workflow, BadgeCheck, Layers, X as XIcon,
  Briefcase, Calculator, Target, UserCog, Building2,
} from 'lucide-react';

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

/* ═══════════════════════════════════════════════════════════════
   WOW MOMENT ANIMATION — 5 scenes, ~12 seconds
   (Preserved per user request)
   ═══════════════════════════════════════════════════════════════ */
const SCENES = [
  { id: 'upload', duration: 2000, copy: 'Connecting your data...' },
  { id: 'mapping', duration: 3000, copy: 'AI understanding your business...' },
  { id: 'model', duration: 2000, copy: 'Modeling relationships...' },
  { id: 'dashboard', duration: 2500, copy: 'Generating your dashboard...' },
  { id: 'query', duration: 3000, copy: 'Ready. Ask anything.' },
];

const COLUMNS = [
  { name: 'Date', type: 'date' },
  { name: 'Revenue', type: 'number' },
  { name: 'Category', type: 'string' },
  { name: 'Region', type: 'string' },
];

function WowAnimation({ onComplete }) {
  const [scene, setScene] = useState(-1);
  const [colIdx, setColIdx] = useState(-1);
  const [queryTyped, setQueryTyped] = useState('');
  const [showCta, setShowCta] = useState(false);
  const navigate = useNavigate();
  const started = useRef(false);

  const startAnimation = useCallback(() => {
    if (started.current) return;
    started.current = true;
    let sceneIdx = 0;
    const advance = () => {
      setScene(sceneIdx);
      if (sceneIdx < SCENES.length - 1) {
        setTimeout(() => { sceneIdx++; advance(); }, SCENES[sceneIdx].duration);
      } else {
        setTimeout(() => { setShowCta(true); onComplete?.(); }, SCENES[sceneIdx].duration);
      }
    };
    advance();
  }, [onComplete]);

  useEffect(() => {
    if (scene !== 1) { setColIdx(-1); return; }
    let i = 0;
    const t = setInterval(() => { setColIdx(i); i++; if (i >= COLUMNS.length) clearInterval(t); }, 600);
    return () => clearInterval(t);
  }, [scene]);

  useEffect(() => {
    if (scene !== 4) { setQueryTyped(''); return; }
    const text = 'Why did revenue spike last quarter?';
    let i = 0;
    const t = setInterval(() => { i++; setQueryTyped(text.slice(0, i)); if (i >= text.length) clearInterval(t); }, 55);
    return () => clearInterval(t);
  }, [scene]);

  const bars = [35, 52, 44, 68, 58, 75, 82];
  const linePts = [20, 28, 25, 38, 35, 48, 55, 62, 58, 72];

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      <div className="rounded-2xl border border-white/10 overflow-hidden bg-[#0a0f1e] shadow-[0_0_60px_rgba(6,182,212,0.08)]">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.05]">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="px-4 py-0.5 rounded-full bg-white/[0.04] text-[9px] text-white/25 font-mono">app.inzara.com</div>
          </div>
        </div>

        <div className="relative min-h-[320px] p-6 flex flex-col items-center justify-center">
          {scene === -1 && (
            <motion.button
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              onClick={startAnimation}
              className="flex flex-col items-center gap-4 group cursor-pointer"
              data-testid="wow-start-btn"
            >
              <div className="w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/20 transition-all">
                <Sparkles className="w-7 h-7 text-cyan-400" />
              </div>
              <span className="text-sm text-slate-400 group-hover:text-white transition-colors">Watch the magic happen</span>
            </motion.button>
          )}

          <AnimatePresence mode="wait">
            {scene === 0 && (
              <motion.div key="upload" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center gap-4 w-full">
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4 }}
                  className="w-full max-w-sm border-2 border-dashed border-cyan-500/30 rounded-xl p-6 text-center bg-cyan-500/[0.03]"
                >
                  <Upload className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                  <motion.div
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
                    className="flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/[0.06] border border-white/10"
                  >
                    <FileSpreadsheet className="w-4 h-4 text-emerald-400" />
                    <span className="text-sm text-white font-mono">sales_data.csv</span>
                    <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ delay: 0.8, duration: 1 }} className="h-0.5 bg-cyan-400 rounded-full absolute bottom-0 left-0" />
                  </motion.div>
                </motion.div>
              </motion.div>
            )}

            {scene === 1 && (
              <motion.div key="mapping" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full max-w-sm space-y-2">
                {COLUMNS.map((col, i) => (
                  <motion.div
                    key={col.name}
                    initial={{ opacity: 0.2, x: -10 }}
                    animate={i <= colIdx ? { opacity: 1, x: 0 } : { opacity: 0.2 }}
                    transition={{ duration: 0.3 }}
                    className="flex items-center justify-between px-4 py-2.5 rounded-lg border transition-all"
                    style={{ borderColor: i <= colIdx ? 'rgba(6,182,212,0.3)' : 'rgba(255,255,255,0.05)', background: i <= colIdx ? 'rgba(6,182,212,0.05)' : 'transparent' }}
                  >
                    <span className="text-sm font-mono text-white">{col.name}</span>
                    <AnimatePresence>
                      {i <= colIdx && (
                        <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="flex items-center gap-1.5">
                          <Check className="w-3.5 h-3.5 text-cyan-400" />
                          <span className="text-[10px] text-cyan-400 uppercase tracking-wider">{col.type}</span>
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </motion.div>
            )}

            {scene === 2 && (
              <motion.div key="model" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex items-center justify-center gap-6">
                {['Sales', 'Products', 'Regions'].map((t, i) => (
                  <React.Fragment key={t}>
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }} animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.3, duration: 0.4 }}
                      className="px-5 py-3 rounded-xl border border-white/10 bg-white/[0.04] text-sm font-mono text-white"
                    >
                      {t}
                    </motion.div>
                    {i < 2 && (
                      <motion.div initial={{ opacity: 0, scaleX: 0 }} animate={{ opacity: 1, scaleX: 1 }} transition={{ delay: (i + 1) * 0.3 + 0.2 }}>
                        <div className="w-8 h-px bg-cyan-500/40" />
                      </motion.div>
                    )}
                  </React.Fragment>
                ))}
              </motion.div>
            )}

            {scene === 3 && (
              <motion.div key="dashboard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full max-w-md">
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[{ label: 'Revenue', val: '$1.2M', change: '+18%' }, { label: 'Orders', val: '8,421', change: '+12%' }, { label: 'Avg Order', val: '$142', change: '+5%' }].map((kpi, i) => (
                    <motion.div key={kpi.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.2 }}
                      className="p-3 rounded-lg border border-white/[0.08] bg-white/[0.03]">
                      <p className="text-[9px] text-slate-500 uppercase">{kpi.label}</p>
                      <p className="text-lg font-bold text-white mt-0.5">{kpi.val}</p>
                      <span className="text-[10px] text-emerald-400">{kpi.change}</span>
                    </motion.div>
                  ))}
                </div>
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                  className="h-32 flex items-end gap-1.5 px-4 py-3 rounded-lg border border-white/[0.08] bg-white/[0.02]">
                  {bars.map((h, i) => (
                    <motion.div key={i} initial={{ height: 0 }} animate={{ height: `${h}%` }} transition={{ delay: 0.8 + i * 0.08, duration: 0.4 }}
                      className="flex-1 rounded-t bg-gradient-to-t from-cyan-500/60 to-cyan-400/80" />
                  ))}
                </motion.div>
              </motion.div>
            )}

            {scene === 4 && (
              <motion.div key="query" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="w-full max-w-md space-y-4">
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl border border-cyan-500/20 bg-white/[0.04]">
                  <Search className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                  <span className="text-sm font-mono text-white">{queryTyped}<span className="animate-pulse text-cyan-400">|</span></span>
                </div>
                {queryTyped.length > 18 && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
                    <div className="h-28 flex items-end px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.02] relative">
                      <svg viewBox="0 0 200 80" className="w-full h-full" preserveAspectRatio="none">
                        <motion.polyline
                          initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.2 }}
                          points={linePts.map((y, i) => `${i * (200 / (linePts.length - 1))},${80 - y}`).join(' ')}
                          fill="none" stroke="#06b6d4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        />
                        <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.2" />
                          <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
                        </linearGradient>
                        <motion.polygon
                          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }}
                          points={`0,80 ${linePts.map((y, i) => `${i * (200 / (linePts.length - 1))},${80 - y}`).join(' ')} 200,80`}
                          fill="url(#areaGrad)"
                        />
                      </svg>
                    </div>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8 }}
                      className="flex items-center gap-2 text-sm text-emerald-400">
                      <TrendingUp className="w-4 h-4" />
                      <span>Revenue trending upward &mdash; +18% over 6 months</span>
                    </motion.div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {scene >= 0 && (
            <motion.p key={scene} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="absolute bottom-4 left-0 right-0 text-center text-xs text-slate-500">
              {SCENES[scene]?.copy}
            </motion.p>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showCta && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center mt-6">
            <button onClick={() => navigate('/register')} className="text-sm text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1.5 mx-auto group" data-testid="wow-cta">
              Start your free trial <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   INTERACTIVE QUERY — AI ANALYST COPILOT DEMO
   ═══════════════════════════════════════════════════════════════ */
const LOADING_MSGS = ['Mapping business entities...', 'Detecting trends...', 'Modeling drivers...', 'Generating visualization...', 'Surfacing insights...'];
const FOLLOW_UPS = ['Show breakdown by segment', 'Compare to last quarter', "What's driving the increase?"];

/* Persona-tailored AI Copilot demo presets — populated when a persona card is clicked. */
const COPILOT_PRESETS = {
  cio: {
    label: 'CIO / CTO',
    question: 'How are our analytics costs trending vs adoption?',
    headline: 'BI spend is down 38% while active users tripled.',
    bars: [
      { label: 'INZARA SaaS', val: 92, color: '#06b6d4' },
      { label: 'Legacy BI', val: 22, color: '#475569' },
      { label: 'ETL tools', val: 18, color: '#475569' },
      { label: 'Custom apps', val: 12, color: '#475569' },
      { label: 'Services', val: 8, color: '#475569' },
    ],
    narrative: 'Consolidating onto INZARA cut annual tooling cost from $680K to $420K while monthly active analysts grew from 84 to 247. Cost per active user dropped 76% — recommend retiring the legacy on-prem BI by Q4.',
    followUps: ['Break down by department', 'Show TCO comparison', 'Forecast next year spend'],
    chartLabel: 'Monthly tooling spend, last 12 months',
  },
  cfo: {
    label: 'CFO / Finance',
    question: 'What is driving variance vs forecast this quarter?',
    headline: 'Net revenue is +6% vs forecast — driven by enterprise expansion and lower churn.',
    bars: [
      { label: 'Expansion', val: 88, color: '#06b6d4' },
      { label: 'Retention', val: 64, color: '#3b82f6' },
      { label: 'New ARR', val: 48, color: '#8b5cf6' },
      { label: 'FX impact', val: 28, color: '#6366f1' },
      { label: 'Discount', val: 14, color: '#475569' },
    ],
    narrative: 'Expansion revenue beat forecast by 14%, contributing $2.4M to the upside. Enterprise gross retention improved 220bps to 96.4%. Recommend updating FY guidance and reallocating CS capacity to enterprise.',
    followUps: ['Drill into expansion deals', 'Compare to prior year', 'Show retention trends'],
    chartLabel: 'Quarterly net revenue vs forecast',
  },
  coo: {
    label: 'COO / Operations',
    question: 'Why is on-time delivery slipping in the western region?',
    headline: 'Western OTD dropped 7 pts — 91% of the slip is in two distribution centers.',
    bars: [
      { label: 'DC-Phoenix', val: 88, color: '#ef4444' },
      { label: 'DC-Seattle', val: 76, color: '#f59e0b' },
      { label: 'DC-Denver', val: 36, color: '#06b6d4' },
      { label: 'DC-LA', val: 30, color: '#06b6d4' },
      { label: 'DC-SF', val: 24, color: '#06b6d4' },
    ],
    narrative: 'Phoenix shows a 12-day backlog on inbound. Seattle is short two driver routes. Recommend re-balancing inbound to Denver and standing up a second Seattle route — projected OTD recovery in 14 days.',
    followUps: ['Show driver shortages', 'Compare to last month', 'Forecast next 30 days'],
    chartLabel: 'On-time delivery trend — west region',
  },
  revenue: {
    label: 'VP Revenue',
    question: 'Where is pipeline conversion strongest right now?',
    headline: 'Mid-market expansion converts 2.4× faster than new logo enterprise.',
    bars: [
      { label: 'MM Expand', val: 94, color: '#06b6d4' },
      { label: 'SMB New', val: 68, color: '#3b82f6' },
      { label: 'ENT Expand', val: 54, color: '#8b5cf6' },
      { label: 'ENT New', val: 38, color: '#6366f1' },
      { label: 'PLG', val: 22, color: '#475569' },
    ],
    narrative: 'Mid-market expansion closes in 28 days on average vs 67 for new enterprise logos. Recommend shifting two AE seats from ENT-new to MM-expansion for Q4 — projected ARR lift of $1.1M.',
    followUps: ['Top accounts to target', 'Win rate by rep', 'Conversion by source'],
    chartLabel: 'Pipeline velocity by segment',
  },
  analytics: {
    label: 'Director of Analytics',
    question: 'Which governed metrics are diverging across teams?',
    headline: '3 of 14 governed KPIs show definition drift between business units.',
    bars: [
      { label: 'ARR Finance', val: 100, color: '#06b6d4' },
      { label: 'ARR Sales', val: 96, color: '#f59e0b' },
      { label: 'NRR Finance', val: 100, color: '#06b6d4' },
      { label: 'NRR CS', val: 86, color: '#ef4444' },
      { label: 'Pipe Mkt', val: 100, color: '#06b6d4' },
    ],
    narrative: 'NRR in CS excludes downgrades within 30 days of renewal — diverging 14 pts from the canonical Finance definition. Recommend aligning to Finance and re-publishing the metric with full lineage.',
    followUps: ['Show definition diff', 'Open in lineage', 'Approve canonical version'],
    chartLabel: 'Definition consistency vs canonical (last 90 days)',
  },
  analyst: {
    label: 'Business Analyst',
    question: 'Show me revenue trends and what to investigate next.',
    headline: 'Revenue is accelerating — growth increased 18% over the last 6 months.',
    bars: [
      { label: 'Cat A', val: 82, color: '#06b6d4' },
      { label: 'Cat B', val: 65, color: '#3b82f6' },
      { label: 'Cat C', val: 48, color: '#8b5cf6' },
      { label: 'Cat D', val: 38, color: '#6366f1' },
      { label: 'Cat E', val: 22, color: '#475569' },
    ],
    narrative: 'Revenue is accelerating across the board. Category A led with 32% growth, signaling strong demand. Categories B and C maintained steady momentum while D and E held flat — a potential area for strategic focus.',
    followUps: ['Show breakdown by segment', 'Compare to last quarter', "What's driving the increase?"],
    chartLabel: 'Revenue trend, last 6 months',
  },
};

function WordReveal({ text, delay = 0, className = '' }) {
  const words = text.split(' ');
  return (
    <span className={className}>
      {words.map((word, i) => (
        <motion.span key={i} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: delay + i * 0.06, duration: 0.3 }} className="inline-block mr-[0.3em]">
          {word}
        </motion.span>
      ))}
    </span>
  );
}

function InteractiveQuery({ preset }) {
  const navigate = useNavigate();
  const [state, setState] = useState('idle');
  const [queryText, setQueryText] = useState(preset?.question || 'What caused revenue growth this quarter?');
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [chartPhase, setChartPhase] = useState(0);
  const hasAutoRun = useRef(false);

  const sectionRef = useRef(null);
  useEffect(() => {
    if (hasAutoRun.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAutoRun.current) {
        hasAutoRun.current = true;
        runQuery();
        observer.disconnect();
      }
    }, { threshold: 0.4 });
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const runQuery = () => {
    if (state === 'loading') return;
    setState('loading');
    setLoadingMsg(0);
    setChartPhase(0);
    let i = 0;
    const t = setInterval(() => { i++; if (i < LOADING_MSGS.length) setLoadingMsg(i); else { clearInterval(t); setState('result'); } }, 500);
  };

  // When a persona-driven preset changes, swap the question and re-run.
  useEffect(() => {
    if (!preset) return;
    setQueryText(preset.question);
    setState('loading');
    setLoadingMsg(0);
    setChartPhase(0);
    let i = 0;
    const t = setInterval(() => { i++; if (i < LOADING_MSGS.length) setLoadingMsg(i); else { clearInterval(t); setState('result'); } }, 500);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset?.question]);

  useEffect(() => {
    if (state !== 'result') return;
    const t = setTimeout(() => setChartPhase(1), 2800);
    return () => clearTimeout(t);
  }, [state]);

  const defaultBars = [
    { label: 'Expansion', val: 82, color: '#06b6d4' },
    { label: 'Retention', val: 65, color: '#3b82f6' },
    { label: 'New Logos', val: 48, color: '#8b5cf6' },
    { label: 'Upsell', val: 38, color: '#6366f1' },
    { label: 'Other', val: 22, color: '#475569' },
  ];
  const resultBars = preset?.bars || defaultBars;
  const headline = preset?.headline || 'Revenue grew 18% this quarter — driven primarily by expansion revenue, stronger retention, and higher average deal size.';
  const narrative = preset?.narrative || 'Expansion revenue contributed 41% of the growth, with NRR improving across enterprise accounts. Average deal size rose 14% as more deals included premium tiers. Recommend doubling down on the enterprise motion to sustain momentum.';
  const followUps = preset?.followUps || FOLLOW_UPS;
  const chartLabel = preset?.chartLabel || 'Quarterly revenue trend';
  const linePts = [18, 24, 22, 32, 38, 35, 46, 52, 58, 55, 64, 72];

  return (
    <div ref={sectionRef} className="w-full max-w-2xl mx-auto" data-testid="interactive-query">
      <div className="p-8 md:p-10 rounded-3xl border border-cyan-500/15 bg-cyan-500/[0.03] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[300px] h-[150px] bg-cyan-500/[0.06] blur-[80px] rounded-full" />
        </div>

        <div className="relative z-10">
          <div className="text-center mb-6">
            <h3 className="font-display text-xl sm:text-2xl font-semibold mb-1" data-testid="query-title">Ask any business question</h3>
            <p className="text-sm text-slate-400">Receive immediate answers backed by your data</p>
            {preset && (
              <motion.div
                key={preset.label}
                initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}
                className="inline-flex items-center gap-2 mt-3 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/[0.08]"
                data-testid="active-persona-pill"
              >
                <Sparkles className="w-3 h-3 text-cyan-400" />
                <span className="text-[11px] text-cyan-200 font-medium">Viewing as {preset.label}</span>
              </motion.div>
            )}
          </div>

          <div className="flex items-center gap-2 mb-4">
            <div className="flex-1 relative">
              <input
                value={queryText}
                onChange={e => setQueryText(e.target.value)}
                className="w-full h-12 pl-4 pr-4 rounded-xl bg-white/[0.06] border border-white/10 text-sm text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/30 transition-all"
                data-testid="query-input"
              />
            </div>
            <button
              onClick={runQuery}
              disabled={state === 'loading'}
              className={`h-12 px-6 rounded-xl font-medium text-sm flex-shrink-0 transition-all ${state === 'loading' ? 'bg-slate-700 text-slate-400' : 'bg-white text-black hover:bg-slate-200'}`}
              data-testid="query-run-btn"
            >
              {state === 'loading' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ask AI'}
            </button>
          </div>

          <AnimatePresence>
            {state === 'loading' && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                className="py-8">
                <div className="flex items-center justify-center gap-4 mb-3">
                  <div className="flex gap-0.5">
                    {[0,1,2,3,4,5,6,7].map(i => (
                      <motion.div key={i}
                        animate={{ height: [6, 18, 6], opacity: [0.2, 0.9, 0.2] }}
                        transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.07 }}
                        className="w-[3px] rounded-full bg-gradient-to-t from-cyan-500 to-blue-400"
                        style={{ height: 6 }}
                      />
                    ))}
                  </div>
                  <motion.span key={loadingMsg} initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }}
                    className="text-sm text-slate-300 font-medium">
                    {LOADING_MSGS[loadingMsg]}
                  </motion.span>
                </div>
                <div className="flex justify-center gap-1 opacity-40">
                  {[0,1,2,3,4,5].map(i => (
                    <motion.div key={i}
                      animate={{ opacity: [0, 0.8, 0], x: [0, 12, 24] }}
                      transition={{ repeat: Infinity, duration: 1.2, delay: i * 0.18 }}
                      className="w-1 h-1 rounded-full bg-cyan-400" />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {state === 'result' && (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                <div className="flex items-start gap-2 mb-4 p-3 rounded-lg bg-emerald-500/[0.08] border border-emerald-500/20">
                  <TrendingUp className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <WordReveal key={headline} text={headline} className="text-sm text-emerald-300 font-medium" />
                </div>

                <div className="relative mb-4 overflow-hidden rounded-lg border border-white/[0.08] bg-white/[0.02]">
                  <AnimatePresence mode="wait">
                    {chartPhase === 0 ? (
                      <motion.div key="bars" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="p-4 space-y-2">
                        {resultBars.map((bar, i) => (
                          <div key={bar.label} className="flex items-center gap-3">
                            <span className="text-[10px] text-slate-500 w-16 text-right">{bar.label}</span>
                            <div className="flex-1 h-6 rounded bg-white/[0.04] relative overflow-hidden">
                              <motion.div initial={{ width: 0 }} animate={{ width: `${bar.val}%` }}
                                transition={{ delay: 0.2 + i * 0.1, duration: 0.6, ease: 'easeOut' }}
                                className="h-full rounded" style={{ background: bar.color }} />
                              <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-white/60 font-mono">${(bar.val * 14.6).toFixed(0)}K</span>
                            </div>
                          </div>
                        ))}
                      </motion.div>
                    ) : (
                      <motion.div key="line" initial={{ opacity: 0, scale: 1.02 }} animate={{ opacity: 1, scale: 1 }} className="p-4">
                        <p className="text-[9px] text-slate-500 mb-2 uppercase tracking-wider">{chartLabel}</p>
                        <svg viewBox="0 0 240 80" className="w-full h-28" preserveAspectRatio="none">
                          <defs>
                            <linearGradient id="morph-grad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.25" />
                              <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
                            </linearGradient>
                          </defs>
                          <motion.polygon
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                            points={`0,80 ${linePts.map((y, i) => `${i * (240 / (linePts.length - 1))},${80 - y}`).join(' ')} 240,80`}
                            fill="url(#morph-grad)"
                          />
                          <motion.polyline
                            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.5 }}
                            points={linePts.map((y, i) => `${i * (240 / (linePts.length - 1))},${80 - y}`).join(' ')}
                            fill="none" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                          />
                          <motion.circle
                            initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.5 }}
                            cx={240} cy={80 - linePts[linePts.length - 1]} r="4" fill="#06b6d4"
                          />
                          <motion.circle
                            initial={{ scale: 0 }} animate={{ scale: [0, 1.8, 1] }} transition={{ delay: 1.5, duration: 0.6 }}
                            cx={240} cy={80 - linePts[linePts.length - 1]} r="4" fill="none" stroke="#06b6d4" strokeWidth="1" opacity="0.4"
                          />
                        </svg>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                <div className="mb-5 p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                  <WordReveal
                    key={narrative}
                    text={narrative}
                    delay={0.4}
                    className="text-xs text-slate-400 leading-relaxed"
                  />
                </div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }}>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2">Try a follow-up:</p>
                  <div className="flex flex-wrap gap-2 mb-5">
                    {followUps.map(q => (
                      <button key={q} onClick={() => { setQueryText(q); setState('idle'); setChartPhase(0); }}
                        className="px-3 py-1.5 rounded-lg border border-white/[0.08] bg-white/[0.03] text-xs text-slate-300 hover:border-cyan-500/30 hover:text-white transition-all"
                        data-testid={`followup-${q.slice(0, 10)}`}>
                        <ArrowRight className="w-3 h-3 inline mr-1 text-cyan-400" />{q}
                      </button>
                    ))}
                  </div>
                </motion.div>

                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2 }}
                  className="flex items-center justify-center gap-3 pt-3 border-t border-white/[0.06]">
                  <button onClick={() => navigate('/register')} className="px-6 py-2.5 rounded-full bg-white text-black font-medium text-sm hover:bg-slate-200 transition-colors" data-testid="query-try-own-cta">
                    Start free trial
                  </button>
                  <button onClick={() => navigate('/request-demo')} className="px-6 py-2.5 rounded-full border border-white/20 text-sm text-white hover:bg-white/5 transition-colors" data-testid="query-upload-cta">
                    Schedule demo
                  </button>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PRODUCT SHOT — real product screenshot in browser-chrome frame
   ═══════════════════════════════════════════════════════════════ */
function ProductShot({ src, alt, url = 'app.inzara.com', caption, eager = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative w-full max-w-6xl mx-auto"
      data-testid="product-shot"
    >
      {/* Ambient glow behind the frame */}
      <div className="absolute inset-0 -z-10 pointer-events-none" aria-hidden="true">
        <div className="absolute inset-x-12 -inset-y-8 rounded-[40px] bg-gradient-to-b from-cyan-500/[0.08] via-blue-500/[0.04] to-transparent blur-3xl" />
      </div>
      <div
        className="rounded-2xl border border-white/10 overflow-hidden shadow-[0_30px_80px_-20px_rgba(6,182,212,0.18)]"
        style={{ background: 'rgba(10,15,30,0.8)', backdropFilter: 'blur(20px)' }}
      >
        {/* Window chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/[0.05] bg-[#080d1c]">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="px-4 py-0.5 rounded-full bg-white/[0.04] text-[10px] text-white/30 font-mono">{url}</div>
          </div>
        </div>
        {/* Screenshot */}
        <img
          src={src}
          alt={alt}
          className="w-full block"
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
        />
      </div>
      {caption && (
        <p className="text-xs text-slate-500 text-center mt-4">{caption}</p>
      )}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ANIMATED COUNTER
   ═══════════════════════════════════════════════════════════════ */
function AnimatedCounter({ end, suffix = '', duration = 2000, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(end * eased);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, end, duration]);

  const display = decimals > 0 ? val.toFixed(decimals) : Math.floor(val).toLocaleString();
  return <span ref={ref}>{display}{suffix}</span>;
}

/* ═══════════════════════════════════════════════════════════════
   LANDING PAGE — AI-NATIVE BI PLATFORM
   ═══════════════════════════════════════════════════════════════ */
const VALUE_PROPS = [
  { icon: Brain, title: 'AI-Powered Analysis', desc: 'Ask questions in plain English and receive instant insights backed by your data.' },
  { icon: LayoutDashboard, title: 'Automated Dashboards', desc: 'Generate complete dashboards automatically from any connected source.' },
  { icon: ScrollText, title: 'Executive Reporting', desc: 'Create board-ready reports without hours of manual preparation.' },
  { icon: LineChart, title: 'Predictive Forecasting', desc: 'Anticipate trends and outcomes before they impact performance.' },
  { icon: Shield, title: 'Enterprise Governance', desc: 'Maintain security, permissions, and policy controls at scale.' },
  { icon: Cloud, title: 'Cloud-Native Architecture', desc: 'No servers, no infrastructure, no upgrades. Fully managed SaaS.' },
];

const INTEGRATIONS = [
  'Salesforce', 'HubSpot', 'Snowflake', 'BigQuery', 'PostgreSQL', 'SQL Server',
  'Oracle', 'SAP', 'NetSuite', 'Google Analytics', 'Stripe', 'Shopify',
];

const HOW_STEPS = [
  { num: '1', icon: Database, title: 'Connect Data Sources', desc: 'CRM, ERP, marketing, finance, operations — all in one place.' },
  { num: '2', icon: Network, title: 'AI Understands Your Business', desc: 'Automatically models relationships, entities, and metrics.' },
  { num: '3', icon: MessageSquare, title: 'Ask Questions Naturally', desc: 'Use conversational analytics — no SQL, no report builders.' },
  { num: '4', icon: Sparkles, title: 'Receive Insights Instantly', desc: 'Dashboards, forecasts, reports, and recommendations on demand.' },
];

const DEPLOY_STEPS = [
  { icon: Plug, title: 'Connect Data', desc: 'Connect your systems in minutes.' },
  { icon: Layers, title: 'Configure Metrics', desc: 'Define KPIs and business rules.' },
  { icon: MessageSquare, title: 'Start Asking Questions', desc: 'Generate insights immediately.' },
];

const COMPARE_ROWS = [
  { old: 'Manual dashboard creation', new: 'AI-generated dashboards' },
  { old: 'Complex report building', new: 'Natural language analysis' },
  { old: 'Long deployment cycles', new: 'Rapid cloud deployment' },
  { old: 'Static reporting', new: 'Dynamic AI insights' },
  { old: 'Specialized expertise required', new: 'Business-user friendly' },
  { old: 'Infrastructure management', new: 'Fully managed SaaS' },
  { old: 'Manual investigation', new: 'AI-assisted root cause analysis' },
  { old: 'Separate tools required', new: 'Built-in forecasting' },
];

const ENTERPRISE_CAPS = [
  { icon: BadgeCheck, label: 'Security By Design', desc: 'Hardened controls, isolation, and continuous internal review.' },
  { icon: Lock, label: 'Encryption In Transit & At Rest', desc: 'TLS 1.2+ in transit, AES-256 at rest for all data.' },
  { icon: KeyRound, label: 'Role-Based Access Control', desc: 'Granular permissions across users, teams, and datasets.' },
  { icon: GitBranch, label: 'Single Sign-On (SAML/OIDC)', desc: 'Centralized identity with your existing IdP.' },
  { icon: Eye, label: 'Audit Logging', desc: 'Full activity history for every query, dashboard, and export.' },
  { icon: Workflow, label: 'Data Lineage Tracking', desc: 'Trace every metric back to its source for full trust.' },
  { icon: FileCheck2, label: 'Row-Level Security', desc: 'Restrict data access by user, role, region, or tenant.' },
  { icon: Activity, label: 'High Availability Architecture', desc: 'Resilient multi-region infrastructure with redundancy and failover.' },
];

const METRICS = [
  { val: 100, suffix: 'M+', label: 'Records Processed', sub: 'Across every connected source' },
  { val: 50, suffix: 'K+', label: 'Dashboards Generated', sub: 'Built automatically by AI' },
  { val: 24, suffix: '/7', label: 'Always-On Intelligence', sub: 'Multi-region, redundant infra', decimals: 0 },
  { val: 100, suffix: 'K+', label: 'Insights Delivered', sub: 'To business users every month' },
];

const ROI_CARDS = [
  { metric: 'Up to 90%', title: 'Reduce Reporting Time By Up To 90%', desc: 'Eliminate manual report preparation and dashboard building.' },
  { metric: '10×', title: '10× Faster Insight Generation', desc: 'Move from monthly reviews to always-on intelligence in seconds.' },
  { metric: 'Every Team', title: 'Analytics Accessible To Every Team', desc: 'Business users self-serve answers — no SQL, no training required.' },
  { metric: 'Lower TCO', title: 'Lower Total Cost Of Ownership', desc: 'Replace your BI stack and infrastructure with a single SaaS platform.' },
];

const TESTIMONIALS = [
  {
    quote: 'INZARA reduced the time our team spends creating reports by over 80%. Our analysts now spend their time on strategy, not formatting.',
    name: 'Sarah Chen', title: 'VP Operations', company: 'Meridian Logistics',
    stats: [
      { value: '86%', label: 'Report build time saved' },
      { value: '5 days → 4 hrs', label: 'Monthly close cycle' },
    ],
  },
  {
    quote: 'We replaced three separate BI tools with INZARA. Our executives finally get the answers they need in real time, without waiting on a data team.',
    name: 'Marcus Bell', title: 'Chief Financial Officer', company: 'Northwind Capital',
    stats: [
      { value: '$420K', label: 'Annual BI tooling savings' },
      { value: '3 → 1', label: 'BI platforms consolidated' },
    ],
  },
  {
    quote: 'Natural language analytics changed everything. Every product manager and regional director now asks their own questions and gets reliable answers.',
    name: 'Priya Raman', title: 'Director of Analytics', company: 'Helix Labs',
    stats: [
      { value: '12 → 240', label: 'Active analytics users' },
      { value: '10×', label: 'Faster decision cycles' },
    ],
  },
];

const PERSONAS = [
  { icon: Briefcase, presetKey: 'cio', title: 'CIOs & CTOs',
    desc: 'Replace fragmented BI stacks with one AI-native platform. Lower TCO, faster rollouts, zero infrastructure.' },
  { icon: Calculator, presetKey: 'cfo', title: 'CFOs & Finance Leaders',
    desc: 'Close the books faster with always-on financial dashboards, variance analysis, and forecasting.' },
  { icon: ServerCog, presetKey: 'coo', title: 'COOs & VP Operations',
    desc: 'Track performance, surface anomalies, and act on operational KPIs the moment they shift.' },
  { icon: Target, presetKey: 'revenue', title: 'VP Revenue & Sales',
    desc: 'Understand pipeline drivers, retention, and expansion revenue in plain English — no analyst queue.' },
  { icon: LineChart, presetKey: 'analytics', title: 'Directors of Analytics',
    desc: 'Govern metrics centrally, scale self-serve analytics, and stop being the report-request bottleneck.' },
  { icon: UserCog, presetKey: 'analyst', title: 'Business Analysts',
    desc: 'Move from formatting reports to driving strategy. Let AI build the dashboards while you find the insight.' },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [activePresetKey, setActivePresetKey] = useState(null);

  const handlePersonaClick = (presetKey) => {
    setActivePresetKey(presetKey);
    requestAnimationFrame(() => {
      const el = document.getElementById('ai-analyst');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const activePreset = activePresetKey ? COPILOT_PRESETS[activePresetKey] : null;

  return (
    <div className="min-h-screen bg-[#060918] text-white overflow-x-hidden font-jakarta" data-testid="landing-page">
      {/* Ambient glows */}
      <div className="fixed inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full bg-cyan-500/[0.07] blur-[150px]" />
        <div className="absolute bottom-1/3 right-1/4 w-[400px] h-[400px] rounded-full bg-blue-600/[0.04] blur-[120px]" />
      </div>

      {/* Nav */}
      <nav className="relative z-50 flex items-center justify-between px-6 lg:px-8 py-5 max-w-7xl mx-auto" data-testid="landing-nav">
        <img src="/inzara-dark.png" alt="INZARA" className="h-8 md:h-9 object-contain" />
        <div className="hidden md:flex items-center gap-8">
          <a href="#ai-analyst" className="text-sm text-slate-400 hover:text-white transition-colors">AI Analyst</a>
          <a href="#platform" className="text-sm text-slate-400 hover:text-white transition-colors">Platform</a>
          <a href="#who" className="text-sm text-slate-400 hover:text-white transition-colors">Who It's For</a>
          <a href="#integrations" className="text-sm text-slate-400 hover:text-white transition-colors">Integrations</a>
          <a href="#enterprise" className="text-sm text-slate-400 hover:text-white transition-colors">Enterprise</a>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/login')} className="hidden sm:inline-flex px-5 py-2 text-sm font-medium text-slate-300 hover:text-white transition-all" data-testid="landing-signin-btn">Sign In</button>
          <button onClick={() => navigate('/register')} className="px-5 py-2 text-sm font-medium border border-white/20 rounded-full text-white hover:bg-white/5 transition-all" data-testid="landing-trial-btn">Start Free</button>
        </div>
      </nav>

      {/* ── 1. HERO ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-6 md:pb-10">
        <motion.div className="text-center max-w-4xl mx-auto" initial="hidden" animate="visible" variants={stagger}>
          <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/20 bg-cyan-500/[0.06] mb-6">
            <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs text-cyan-300 font-medium uppercase tracking-wider">The AI-Native Analytics Platform</span>
          </motion.div>
          <motion.h1 variants={fadeUp} className="font-display text-4xl sm:text-5xl lg:text-[4.25rem] font-bold tracking-tighter leading-[1.05] mb-6" data-testid="hero-headline">
            Your Data Team's<br />
            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-teal-300 bg-clip-text text-transparent">AI Analyst.</span>
          </motion.h1>
          <motion.p variants={fadeUp} className="text-lg sm:text-xl text-slate-400 leading-relaxed mb-8 max-w-2xl mx-auto">
            Connect your business data and instantly uncover trends, generate dashboards, answer questions, and forecast outcomes using natural language. No report building. No BI infrastructure. Just insights.
          </motion.p>
          <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 flex-wrap">
            <button onClick={() => navigate('/register')} className="px-10 py-3.5 rounded-full font-semibold text-sm bg-white text-black hover:bg-slate-200 transition-colors shadow-[0_0_30px_rgba(255,255,255,0.1)]" data-testid="hero-primary-cta">Start Analyzing Data Free</button>
            <a href="#ai-analyst" className="px-8 py-3.5 rounded-full font-medium text-sm border border-white/20 text-white hover:bg-white/5 transition-colors" data-testid="hero-secondary-cta">See AI Analytics In Action</a>
          </motion.div>
          <motion.p variants={fadeUp} className="text-xs text-slate-500 mt-5">
            Enterprise-ready analytics built for modern data-driven organizations.
          </motion.p>
        </motion.div>
      </section>

      {/* ── 2. HERO VISUAL — WOW MOMENT ── */}
      <section id="demo" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-12 md:py-16">
        <WowAnimation />
      </section>

      {/* ── 2b. HERO PRODUCT SHOT — KPI dashboard with AI insights ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pb-12 md:pb-16">
        <ProductShot
          src="/screenshots/hero-kpi-dashboard.png"
          alt="INZARA KPI Monitor — 12 live KPIs with sparklines and 4 AI-generated anomaly findings explaining drivers and recommended actions"
          url="app.inzara.com/kpi-monitor"
          caption="Real KPI Monitor — every metric tracked, every anomaly explained, every action recommended."
          eager
        />
      </section>

      {/* ── 3. AI ANALYTICS COPILOT (moved up — strongest product proof) ── */}
      <section id="ai-analyst" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">AI Analytics Copilot</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="ai-analyst-title">Meet your AI analytics copilot</h2>
            <p className="text-slate-400 mt-4 text-lg">Ask any business question and receive immediate answers — visualizations, drivers, and recommendations included.</p>
          </motion.div>
          <motion.div variants={fadeUp}>
            <InteractiveQuery preset={activePreset} />
          </motion.div>
          <div className="mt-12 md:mt-16">
            <ProductShot
              src="/screenshots/ai-copilot-answer.png"
              alt="INZARA Explore — AI Copilot answering a natural language business question with chart and narrative"
              url="app.inzara.com/explore"
              caption="Real session: ask a question, get an AI-written answer with a chart and follow-up suggestions."
            />
          </div>
        </motion.div>
      </section>

      {/* ── 4. VALUE PROP GRID ── */}
      <section id="platform" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="value-prop-title">Business intelligence reimagined with AI</h2>
            <p className="text-slate-400 mt-4 text-lg">Everything your teams need for insights, reporting, and forecasting — in one unified analytics experience.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {VALUE_PROPS.map((vp, i) => {
              const Icon = vp.icon;
              return (
                <motion.div key={vp.title} variants={fadeUp}
                  className="p-7 rounded-2xl border border-white/[0.06] bg-[#0B1021]/60 hover:border-cyan-500/25 hover:bg-cyan-500/[0.02] hover:-translate-y-1 transition-all group"
                  data-testid={`value-prop-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5 group-hover:bg-cyan-500/15 transition-colors">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-white">{vp.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{vp.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* ── 4b. WHO USES INZARA — PERSONAS ── */}
      <section id="who" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Built For Every Decision Maker</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="personas-title">Who uses INZARA?</h2>
            <p className="text-slate-400 mt-4 text-lg">From the boardroom to the analyst's desk — INZARA meets every role where they work.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERSONAS.map((p, i) => {
              const Icon = p.icon;
              const isActive = activePresetKey === p.presetKey;
              return (
                <motion.button
                  key={p.title}
                  variants={fadeUp}
                  onClick={() => handlePersonaClick(p.presetKey)}
                  type="button"
                  className={`text-left p-7 rounded-2xl border bg-gradient-to-br transition-all group cursor-pointer ${isActive
                    ? 'border-cyan-500/40 from-cyan-500/[0.08] to-cyan-500/[0.02] shadow-[0_0_30px_rgba(6,182,212,0.15)]'
                    : 'border-white/[0.06] from-[#0B1021]/80 to-[#0B1021]/40 hover:border-cyan-500/25 hover:from-cyan-500/[0.04]'}`}
                  data-testid={`persona-${i}`}
                  aria-label={`See AI Copilot demo for ${p.title}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl border flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-cyan-500/20 border-cyan-500/40' : 'bg-cyan-500/10 border-cyan-500/20 group-hover:bg-cyan-500/15'}`}>
                      <Icon className="w-5 h-5 text-cyan-400" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold mb-2 text-white">{p.title}</h3>
                      <p className="text-sm text-slate-400 leading-relaxed">{p.desc}</p>
                      <span className={`inline-flex items-center gap-1 mt-4 text-xs font-medium transition-colors ${isActive ? 'text-cyan-300' : 'text-cyan-400/70 group-hover:text-cyan-300'}`}>
                        {isActive ? 'Showing in copilot below' : 'See it in the AI copilot'}
                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                      </span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* ── 5. INTEGRATIONS ── */}
      <section id="integrations" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-12 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Integrations</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="integrations-title">Connect to the tools you already use</h2>
            <p className="text-slate-400 mt-4 text-lg">Bring together data from every system without complex integration projects.</p>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
            {INTEGRATIONS.map((name, i) => (
              <motion.div key={name} variants={fadeUp}
                className="flex items-center justify-center gap-2 px-4 py-4 rounded-xl border border-white/[0.06] bg-[#0B1021]/60 hover:border-cyan-500/20 hover:bg-cyan-500/[0.03] transition-all"
                data-testid={`integration-${i}`}>
                <Plug className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0" />
                <span className="text-sm text-slate-200 font-medium truncate">{name}</span>
              </motion.div>
            ))}
          </div>
          <motion.div variants={fadeUp} className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-cyan-500/20 bg-cyan-500/[0.06]" data-testid="integrations-badge">
              <Sparkles className="w-3.5 h-3.5 text-cyan-400" />
              <span className="text-sm text-cyan-300 font-medium">100+ connectors available</span>
            </div>
          </motion.div>
          <div className="mt-12 md:mt-16">
            <ProductShot
              src="/screenshots/integrations-sources.png"
              alt="INZARA Data Sources — Snowflake, CSV, API, Excel and prepared datasets connected and managed in one place"
              url="app.inzara.com/data-sources"
              caption="Connect databases, warehouses, and files — monitor status and tables from one console."
            />
          </div>
        </motion.div>
      </section>

      {/* ── 6. HOW IT WORKS — 4 STEPS ── */}
      <section id="how" className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-16">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">How It Works</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="how-title">From raw data to executive insight — in minutes</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {HOW_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.num} variants={fadeUp} className="relative" data-testid={`how-step-${i}`}>
                  {i < HOW_STEPS.length - 1 && (
                    <div className="hidden lg:block absolute top-7 -right-4 z-20">
                      <ChevronRight className="w-6 h-6 text-cyan-500/30" />
                    </div>
                  )}
                  <div className="p-6 rounded-2xl border border-white/[0.06] bg-[#0B1021]/60 h-full">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                        <Icon className="w-4.5 h-4.5 text-cyan-400" />
                      </div>
                      <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">Step {s.num}</span>
                    </div>
                    <h3 className="text-base font-semibold text-white mb-2">{s.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* ── 7. DEPLOYMENT STORY ── */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Deployment</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="deploy-title">Go live in days, not months</h2>
            <p className="text-slate-400 mt-4 text-lg">Traditional BI deployments require infrastructure, configuration, and ongoing maintenance. INZARA is ready from day one.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {DEPLOY_STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div key={s.title} variants={fadeUp}
                  className="p-7 rounded-2xl border border-white/[0.06] bg-[#0B1021]/60 hover:border-cyan-500/20 transition-all"
                  data-testid={`deploy-step-${i}`}>
                  <div className="w-11 h-11 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-5">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </motion.div>
              );
            })}
          </div>
          <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-10">
            {['No servers', 'No upgrades', 'No maintenance', 'No BI administration'].map(item => (
              <div key={item} className="flex items-center gap-2 text-sm text-slate-400">
                <Check className="w-4 h-4 text-cyan-400" /> {item}
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── 8. COMPARISON ── */}
      <section className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="compare-title">Why teams are moving beyond traditional BI</h2>
            <p className="text-slate-400 mt-4 text-lg">From manual, complex, and slow — to AI-driven, simple, and instant.</p>
          </motion.div>
          <motion.div variants={fadeUp} className="rounded-2xl border border-cyan-500/15 overflow-hidden shadow-[0_0_40px_rgba(6,182,212,0.05)]" data-testid="compare-table">
            <div className="grid grid-cols-[1fr_auto_1fr] bg-white/[0.02]">
              <div className="p-5 border-b border-white/[0.06] flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                  <XIcon className="w-3 h-3 text-red-400" strokeWidth={3} />
                </span>
                <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Traditional BI</span>
              </div>
              <div className="border-b border-white/[0.06]" />
              <div className="p-5 border-b border-cyan-500/20 bg-gradient-to-r from-cyan-500/[0.08] to-blue-500/[0.06] flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-cyan-500/15 border border-cyan-500/40 flex items-center justify-center shadow-[0_0_10px_rgba(6,182,212,0.4)]">
                  <Sparkles className="w-3 h-3 text-cyan-300" />
                </span>
                <span className="text-xs uppercase tracking-wider text-cyan-300 font-semibold">INZARA Platform</span>
              </div>
            </div>
            {COMPARE_ROWS.map((row, idx) => (
              <div key={row.old} className="grid grid-cols-[1fr_auto_1fr] hover:bg-white/[0.01] transition-colors">
                <div className="p-5 border-b border-white/[0.04] text-sm text-slate-400 flex items-center gap-3">
                  <XIcon className="w-4 h-4 text-red-400/70 flex-shrink-0" strokeWidth={2.5} />
                  <span className="line-through decoration-red-500/30 decoration-1">{row.old}</span>
                </div>
                <div className="border-b border-white/[0.04] flex items-center px-1">
                  <ChevronRight className="w-3.5 h-3.5 text-cyan-500/40" />
                </div>
                <div className="p-5 border-b border-cyan-500/[0.08] bg-gradient-to-r from-cyan-500/[0.06] to-blue-500/[0.04] text-sm text-cyan-50 font-semibold flex items-center gap-3">
                  <Check className="w-4 h-4 text-cyan-400 flex-shrink-0" strokeWidth={3} />
                  {row.new}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── 9. BEFORE vs AFTER WORKFLOW ── */}
      <section className="relative z-10 max-w-6xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Workflow</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="workflow-title">From investigation to insight</h2>
            <p className="text-slate-400 mt-4 text-lg">Same question. Two completely different experiences.</p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Traditional BI */}
            <motion.div variants={fadeUp} className="p-7 rounded-2xl border border-white/[0.06] bg-[#0B1021]/60" data-testid="workflow-traditional">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-slate-700/40 border border-slate-600/40 flex items-center justify-center">
                  <Clock className="w-4 h-4 text-slate-400" />
                </div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Traditional BI</span>
              </div>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Question</p>
              <p className="text-base text-white font-medium mb-5">Why did revenue decline?</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Steps</p>
              <ul className="space-y-2 mb-6">
                {['Open dashboard', 'Filter reports', 'Build analysis', 'Export data', 'Investigate manually'].map((step, i) => (
                  <li key={step} className="flex items-center gap-3 text-sm text-slate-400">
                    <span className="w-5 h-5 rounded-full bg-slate-700/40 border border-slate-600/40 flex items-center justify-center text-[10px] font-bold text-slate-400 flex-shrink-0">{i + 1}</span>
                    {step}
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Time required</span>
                <span className="text-2xl font-bold text-slate-300">45 Minutes</span>
              </div>
            </motion.div>

            {/* INZARA */}
            <motion.div variants={fadeUp} className="p-7 rounded-2xl border border-cyan-500/25 bg-gradient-to-b from-cyan-500/[0.06] to-cyan-500/[0.02] relative overflow-hidden" data-testid="workflow-inzara">
              <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
                <div className="absolute top-0 right-0 w-[200px] h-[150px] bg-cyan-500/[0.08] blur-[80px] rounded-full" />
              </div>
              <div className="relative">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-[10px] font-bold text-cyan-300 uppercase tracking-wider">With INZARA</span>
                </div>
                <p className="text-xs text-cyan-400/70 uppercase tracking-wider mb-2">Question</p>
                <p className="text-base text-white font-medium mb-5">Why did revenue decline?</p>
                <p className="text-xs text-cyan-400/70 uppercase tracking-wider mb-3">AI Response</p>
                <div className="p-4 rounded-lg bg-white/[0.04] border border-cyan-500/15 mb-5">
                  <p className="text-sm text-emerald-300 font-medium mb-3 flex items-start gap-2">
                    <TrendingUp className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Revenue declined 8%.
                  </p>
                  <p className="text-xs text-slate-400 mb-2">Primary drivers:</p>
                  <ul className="space-y-1.5 mb-3">
                    {['Reduced enterprise renewals', 'Lower expansion revenue', 'Seasonal slowdown'].map(d => (
                      <li key={d} className="flex items-start gap-2 text-sm text-slate-300">
                        <Check className="w-3.5 h-3.5 text-cyan-400 mt-0.5 flex-shrink-0" />
                        {d}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-slate-500 italic">Forecast impact identified. Recommended actions generated.</p>
                </div>
                <div className="pt-4 border-t border-cyan-500/15 flex items-center justify-between">
                  <span className="text-xs text-cyan-400/70 uppercase tracking-wider">Time required</span>
                  <span className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">30 Seconds</span>
                </div>
              </div>
            </motion.div>
          </div>
          <div className="mt-14 md:mt-20">
            <ProductShot
              src="/screenshots/workflow-lineage.png"
              alt="INZARA Data Lineage — every field, calculation, and entity traced from source to dashboard"
              url="app.inzara.com/lineage"
              caption="Field-level lineage powers root-cause analysis — every metric traces back to its source."
            />
          </div>
        </motion.div>
      </section>

      {/* ── 10. ENTERPRISE CAPABILITIES (expanded) ── */}
      <section id="enterprise" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Built For The Enterprise</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="enterprise-title">Enterprise analytics without enterprise complexity</h2>
            <p className="text-slate-400 mt-4 text-lg">Security, governance, and scale — engineered into every layer of the platform.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {ENTERPRISE_CAPS.map((cap, i) => {
              const Icon = cap.icon;
              return (
                <motion.div key={cap.label} variants={fadeUp}
                  className="p-5 rounded-xl border border-white/[0.06] bg-[#0B1021]/60 hover:border-cyan-500/25 hover:bg-cyan-500/[0.02] transition-all"
                  data-testid={`enterprise-cap-${i}`}>
                  <div className="w-9 h-9 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center mb-3">
                    <Icon className="w-4 h-4 text-cyan-400" />
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-1.5">{cap.label}</h3>
                  <p className="text-xs text-slate-400 leading-relaxed">{cap.desc}</p>
                </motion.div>
              );
            })}
          </div>
          <div className="mt-14 md:mt-20">
            <ProductShot
              src="/screenshots/enterprise-rbac.png"
              alt="INZARA Access Control — Role-Based Access, ABAC policies, Row-Level Security, and complete activity logging"
              url="app.inzara.com/access-control"
              caption="RBAC, ABAC, and row-level security configured visually — no scripts, no service tickets."
            />
          </div>
        </motion.div>
      </section>

      {/* ── 11. SOCIAL PROOF — METRICS ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-24">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-12">
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-200" data-testid="social-proof-title">Powering data-driven organizations</h2>
          </motion.div>
          <motion.div variants={fadeUp} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-14">
            {METRICS.map((m, i) => (
              <div key={m.label} className="p-8 rounded-2xl border border-white/[0.06] bg-gradient-to-b from-[#0B1021]/90 to-[#0B1021]/40 text-center hover:border-cyan-500/25 transition-colors" data-testid={`metric-${i}`}>
                <p className="font-display text-5xl sm:text-6xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent leading-none">
                  <AnimatedCounter end={m.val} suffix={m.suffix} decimals={m.decimals || 0} />
                </p>
                <p className="text-sm sm:text-base text-white font-semibold mt-4 leading-tight">{m.label}</p>
                {m.sub && <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{m.sub}</p>}
              </div>
            ))}
          </motion.div>
          {/* Logo placeholders */}
          <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 opacity-50">
            {['ACME GROUP', 'NORTHWIND', 'ATLAS CO.', 'VERTEX AI', 'MERIDIAN', 'HELIX LABS'].map(name => (
              <span key={name} className="text-xs font-bold tracking-[0.2em] text-slate-500">{name}</span>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── 12. ROI / OUTCOMES ── */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Business Outcomes</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="roi-title">Faster insights. Better decisions.</h2>
            <p className="text-slate-400 mt-4 text-lg">Measurable impact across reporting time, decision speed, analytics adoption, and total cost of ownership.</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ROI_CARDS.map((card, i) => (
              <motion.div key={card.title} variants={fadeUp}
                className="p-7 rounded-2xl border border-white/[0.06] bg-gradient-to-b from-[#0B1021]/80 to-[#0B1021]/40 hover:border-cyan-500/25 transition-all"
                data-testid={`roi-card-${i}`}>
                <p className="font-display text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-3">{card.metric}</p>
                <h3 className="text-base font-semibold text-white mb-2">{card.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-14 md:mt-20">
            <ProductShot
              src="/screenshots/outcomes-insights.png"
              alt="INZARA Insights catalog — dashboards and reports automatically generated and organized for every team"
              url="app.inzara.com/dashboards"
              caption="Every team gets the dashboards and reports they need — auto-generated, organized, and shareable."
            />
          </div>
        </motion.div>
      </section>

      {/* ── 13. TESTIMONIALS ── */}
      <section id="testimonials" className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-20 md:py-28">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-80px' }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14 max-w-3xl mx-auto">
            <p className="text-sm uppercase tracking-[0.2em] text-cyan-500 font-semibold mb-3">Customer Stories</p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight" data-testid="testimonials-title">Loved by data leaders and executives</h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={t.name} variants={fadeUp}
                className="p-7 rounded-2xl border border-white/[0.06] bg-[#0B1021]/60 hover:border-cyan-500/25 hover:bg-cyan-500/[0.02] transition-all flex flex-col"
                data-testid={`testimonial-${i}`}>
                <Quote className="w-6 h-6 text-cyan-400/40 mb-4" />
                <p className="text-sm text-slate-200 leading-relaxed mb-5 flex-1">&ldquo;{t.quote}&rdquo;</p>
                {/* Measurable outcome stats */}
                {t.stats && (
                  <div className="grid grid-cols-2 gap-2 mb-5 pt-4 border-t border-white/[0.06]">
                    {t.stats.map(s => (
                      <div key={s.label} className="px-3 py-2.5 rounded-lg bg-gradient-to-br from-cyan-500/[0.08] to-cyan-500/[0.02] border border-cyan-500/15">
                        <p className="font-display text-lg font-bold bg-gradient-to-r from-cyan-300 to-blue-300 bg-clip-text text-transparent leading-tight">{s.value}</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wider mt-1 leading-tight">{s.label}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 pt-3 border-t border-white/[0.06]">
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-cyan-500/30 to-blue-500/20 border border-cyan-500/30 flex items-center justify-center text-sm font-bold text-cyan-200">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{t.name}</p>
                    <p className="text-xs text-slate-400 truncate">{t.title} &middot; {t.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* ── 14. FINAL CTA ── */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pb-24 md:pb-32 pt-8">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
          className="p-10 md:p-16 rounded-3xl border border-white/[0.08] text-center relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(6,182,212,0.06) 0%, rgba(59,130,246,0.06) 100%)' }}
          data-testid="final-cta-section">
          <div className="absolute inset-0 pointer-events-none"><div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-cyan-500/[0.08] blur-[100px] rounded-full" /></div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mb-4 relative z-10" data-testid="final-cta-title">See what your data has been trying to tell you</h2>
          <p className="text-slate-400 mb-8 relative z-10 text-lg">Experience AI-powered analytics that helps every team make faster, smarter decisions.</p>
          <div className="flex items-center justify-center gap-4 flex-wrap relative z-10">
            <button onClick={() => navigate('/register')} className="px-10 py-3.5 rounded-full font-semibold text-sm bg-white text-black hover:bg-slate-200 transition-colors shadow-[0_0_30px_rgba(255,255,255,0.1)]" data-testid="final-primary-cta">Start Analyzing Data Free</button>
            <button onClick={() => navigate('/request-demo')} className="px-8 py-3.5 rounded-full font-medium text-sm border border-white/20 text-white hover:bg-white/5 transition-colors" data-testid="final-secondary-cta">Schedule Personalized Demo</button>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mt-8 relative z-10">
            <div className="flex items-center gap-2 text-xs text-slate-500"><Check className="w-3.5 h-3.5 text-cyan-400" /> No credit card required</div>
            <div className="flex items-center gap-2 text-xs text-slate-500"><Check className="w-3.5 h-3.5 text-cyan-400" /> Fully managed SaaS</div>
            <div className="flex items-center gap-2 text-xs text-slate-500"><Check className="w-3.5 h-3.5 text-cyan-400" /> Enterprise-grade security</div>
            <div className="flex items-center gap-2 text-xs text-slate-500"><Check className="w-3.5 h-3.5 text-cyan-400" /> Rapid deployment</div>
          </div>
        </motion.div>
      </section>

      {/* ── SEO / About + FAQ — substantive, keyword-rich copy ── */}
      <section className="relative z-10 max-w-4xl mx-auto px-6 lg:px-8 pb-24" data-testid="landing-seo-section">
        <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-4">The AI-native business intelligence platform</h2>
        <p className="text-slate-400 leading-relaxed mb-4">
          INZARA is a modern business intelligence and analytics platform built for teams that want
          answers, not backlogs. Connect your data warehouse — Snowflake, BigQuery, Redshift,
          Databricks, ClickHouse, PostgreSQL, MySQL, or DuckDB — and let everyone from analysts to
          executives explore live data in plain English. INZARA pairs a governed semantic layer with
          an AI copilot so every dashboard, report, and metric stays accurate, secure, and consistent
          across the whole organization.
        </p>
        <p className="text-slate-400 leading-relaxed mb-10">
          Teams use INZARA to replace slow, fragmented reporting and legacy BI tools like Tableau,
          Power BI, and Looker with self-service analytics, automated dashboards, AI-generated
          insights, root-cause investigations, KPI monitoring, and scheduled alerts delivered to
          email, Slack, and Microsoft Teams — all under enterprise-grade governance, row-level
          security, and full data lineage.
        </p>
        <div className="space-y-6">
          {[
            ['What is INZARA used for?', 'INZARA is used for self-service business intelligence: exploring data in natural language, building interactive dashboards and reports, monitoring KPIs, investigating why metrics change, and sharing governed insights across every team.'],
            ['Which data sources does INZARA support?', 'INZARA connects natively to Snowflake, BigQuery, Redshift, Databricks, ClickHouse, PostgreSQL, MySQL, and DuckDB, with query push-down for fast performance on large, even billion-row, tables.'],
            ['How is INZARA different from traditional BI tools?', 'INZARA is AI-native. Instead of manually building every chart, you ask questions in plain English and get governed answers, auto-generated dashboards, and AI-driven root-cause analysis — while a certified semantic layer keeps metrics trustworthy.'],
            ['Is INZARA secure and enterprise-ready?', 'Yes. INZARA queries your warehouse directly with role-based access control, row-level security, audit logging, and cross-asset lineage, so you keep full control of data and permissions.'],
            ['Can I try INZARA for free?', 'Yes — start a free trial with no credit card required, or book a personalized demo to see INZARA applied to your own data and use cases.'],
          ].map(([q, a]) => (
            <div key={q}>
              <h3 className="font-semibold text-white mb-1.5">{q}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/[0.05] py-10 px-6 lg:px-8" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 mb-8">
          <div className="col-span-2 sm:col-span-1">
            <div className="flex items-center gap-2.5 mb-3">
              <img src="/inzara-dark.png" alt="INZARA" className="h-6 object-contain" />
            </div>
            <p className="text-[11px] text-slate-500 leading-relaxed">The AI-native analytics platform. Business Intelligence, reimagined.</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-300 mb-3 uppercase tracking-wider">Developers</p>
            <ul className="space-y-2">
              <li><a href="/developers" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-developer-portal">Developer Portal</a></li>
              <li><a href="/embed-playground" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-embed-playground">Embed Playground</a></li>
              <li><a href="/api/mgmt/v1/openapi.json" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-openapi">OpenAPI Spec</a></li>
              <li><a href="https://www.npmjs.com/package/@inzara/embed-react" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-sdk-react">@inzara/embed-react</a></li>
              <li><a href="https://www.npmjs.com/package/@inzara/embed-js" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-sdk-js">@inzara/embed-js</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-300 mb-3 uppercase tracking-wider">Resources</p>
            <ul className="space-y-2">
              <li><a href="/help?s=embed-manager" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-embed-docs">Embed SDK Docs</a></li>
              <li><a href="/help" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-help">Product Documentation</a></li>
              <li><a href="/api/docs" target="_blank" rel="noreferrer" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-api-docs">API Reference (Swagger)</a></li>
              <li><a href="/request-demo" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-demo">Request a Demo</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold text-slate-300 mb-3 uppercase tracking-wider">Company</p>
            <ul className="space-y-2">
              <li><a href="/register" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-signup">Start Free</a></li>
              <li><a href="/login" className="text-xs text-slate-400 hover:text-cyan-300 transition-colors" data-testid="landing-footer-login">Sign In</a></li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3 pt-6 border-t border-white/[0.04]">
          <span className="text-xs text-slate-600 font-medium">INZARA &mdash; The AI-Native Analytics Platform</span>
          <p className="text-[11px] text-slate-600">&copy; {new Date().getFullYear()} INZARA. Business Intelligence Reimagined with AI.</p>
        </div>
      </footer>
    </div>
  );
}
